// JOBE-0101: useEntityBrowserState — the state composition hook
// exposed alongside <EntityBrowser>. Surfaces that need bespoke
// composition (e.g. the Projects Index with a custom row + side rail)
// consume the hook directly and assemble their own JSX; the host
// component is the default consumer.
//
// Returns: { chips, sort, cursor, view, setChips, setSort, setCursor,
//            setView, clearAll, rows, pages, nextCursor, kindSummary,
//            loadState, error, retry, selectedUrn, setSelectedUrn }
//
// Inputs:
//   - `anchor`: optional `{ inProject?, ... }` filter merged into the
//     /api/entities query alongside chip-derived filters.
//   - `urlStateNamespace`: optional non-empty, dot-free string. When
//     set, the underlying URL state prefixes every key it owns with
//     `<namespace>.`. The hook throws at mount if the namespace
//     contains a `.` (composition mistake — see spec §Decision 3).
//
// Selection is **single-URN** (today's library shape). Per spec
// §Decision 2: the host owns selection when it renders the default
// inspector; surfaces that pass a custom inspector can lift selection
// out via this hook's `selectedUrn` / `setSelectedUrn`.

import { useCallback, useState } from 'react';
import { useEntityBrowserUrlState } from './useEntityBrowserUrlState.js';
import { useEntityBrowserQuery } from './useEntityBrowserQuery.js';

export function useEntityBrowserState({ anchor, urlStateNamespace } = {}) {
  const url = useEntityBrowserUrlState({ namespace: urlStateNamespace });
  const {
    rows, pages, nextCursor, kindSummary, loadState, error, retry,
  } = useEntityBrowserQuery({
    chips: url.chips,
    sort: url.sort,
    cursor: url.cursor,
    anchor,
  });

  // Selection state is local-only per umbrella §R6 / spec §Decision 2.
  // Today the library's selection is not URL-stateful; a future spec
  // that lifts it into the URL would join the namespaced set
  // automatically (the forward note on `?entity=` in spec §Decision 3).
  const [selectedUrn, setSelectedUrn] = useState(null);
  const toggleSelectedUrn = useCallback((urn) => {
    setSelectedUrn((prev) => (prev === urn ? null : urn));
  }, []);

  return {
    // URL-owned chip/sort/cursor/view state
    chips: url.chips,
    sort: url.sort,
    cursor: url.cursor,
    view: url.view,
    setChips: url.setChips,
    setSort: url.setSort,
    setCursor: url.setCursor,
    setView: url.setView,
    clearAll: url.clearAll,
    // Query-owned page state
    rows,
    pages,
    nextCursor,
    kindSummary,
    loadState,
    error,
    retry,
    // Selection
    selectedUrn,
    setSelectedUrn,
    toggleSelectedUrn,
  };
}
